import { ref, computed, watch, markRaw } from "vue";
import { defineStore } from "pinia";
import type { ShortDocument } from "@kekkle/shared/types/documents/short-document/short-document";
import {
  subscribeToShorts as subscribeToShortsService,
  addShort as addShortService,
  deleteShort as deleteShortService,
  reportShort as reportShortService,
  markShortAsViewed as markShortAsViewedService,
} from "@/helpers/firestore/ShortService";
import { useUserStore } from "@/stores/modules/user/user";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useShortFilterStore } from "./shorts-filter";
import { firebaseStorage } from "@/helpers/firebase/firebase";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import {
  getShortVideoFromFirestorage,
  getShortThumbnailFromFirestorage,
  deleteShortMedia,
} from "@/helpers/firestorage/ShortMediaService";
import {
  onCacheInvalidated,
  clearCachedImages,
} from "@/helpers/cache/image-cache";
import {
  getCachedDocuments,
  setCachedDocuments,
} from "@/helpers/cache/document-cache";
import { CacheTypes } from "@/enums/cache-types";
import { SHORT_IN_MEMORY_MAX_VIDEOS } from "@/constants/shorts";
import {
  onShortRecorded,
  onShortTransferStart,
  onShortTransferProgress,
  onShortTransferError,
  onShortCameraDismissed,
  abortActiveShortTransfer,
} from "@/helpers/flutter/features/shorts-bridge";
import { translate } from "@/i18n/vueI18n";
import { handleFirestoreException } from "@/helpers/exceptions/handler";
import { setErrorToast } from "@/helpers/notifications/toast";
import type { FirestoreError } from "firebase/firestore";

export type StoredShortMedia = {
  blob: Blob;
  url: string;
};

export const useShortsStore = defineStore("shorts", () => {
  const userStore = useUserStore();
  const authStore = useAuthStore();
  const filterStore = useShortFilterStore();

  /* State */
  const shortsByGroup = ref<Record<string, ShortDocument[]>>({});
  const loadingByGroup = ref<Record<string, boolean>>({});
  const isStaleByGroup = ref<Record<string, boolean>>({});
  const isTransferringFromNative = ref(false);
  const nativeTransferProgress = ref(0);
  const nativeTransferTotalBytes = ref(0);
  const recordedVideoFile = ref<File | null>(null);
  // Short the viewer was opened with; see `activeShort` for the live version
  const openedShort = ref<ShortDocument | null>(null);
  const viewerOpen = ref(false);
  const videosById = ref<Record<string, StoredShortMedia>>({});
  const thumbnailsById = ref<Record<string, StoredShortMedia>>({});
  const inFlightVideoLoads = new Map<string, Promise<string>>();
  const inFlightThumbnailLoads = new Map<string, Promise<string>>();
  const sessionViewedShortIds = ref<Set<string>>(new Set());

  // In-memory video LRU tracking (max 4 videos)
  let videoAccessOrder: string[] = [];

  /* Listener management */
  let activeUnsubscribe: (() => void) | null = null;
  let subscribedGroupId: string | null = null;

  /* Helper functions */
  function isSameDay(a: Date, b: Date): boolean {
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  /* Computed */
  const activeGroupId = computed(() => userStore.activeGroup);

  /** All shorts for the active group (unfiltered, from onSnapshot) */
  const allShorts = computed<ShortDocument[]>(() => {
    if (!activeGroupId.value) return [];
    return shortsByGroup.value[activeGroupId.value] || [];
  });

  /**
   * Short shown in the viewer, kept in sync with the listener so changes like
   * the comment counter show up while it is open. Falls back to the opened
   * copy when the short is not (or no longer) in the list.
   */
  const activeShort = computed<ShortDocument | null>(() => {
    const opened = openedShort.value;
    if (!opened) return null;
    return allShorts.value.find((s) => s._id === opened._id) ?? opened;
  });

  /** Filtered shorts - what the view consumes */
  const shorts = computed<ShortDocument[]>(() => {
    const params = filterStore.getFilterParams();
    let result = allShorts.value;

    if (params.authorUuid) {
      result = result.filter((s) => s.created_by === params.authorUuid);
    }

    if (params.date) {
      const filterDate = params.date;
      result = result.filter((s) => isSameDay(s.timestamp, filterDate));
    }

    return result;
  });

  const loading = computed<boolean>(() => {
    if (!activeGroupId.value) return false;
    return loadingByGroup.value[activeGroupId.value] ?? false;
  });

  const isStale = computed<boolean>(() => {
    if (!activeGroupId.value) return false;
    return isStaleByGroup.value[activeGroupId.value] ?? false;
  });

  /**
   * Group active group shorts by author UUID (derived from allShorts)
   */
  const shortsByAuthor = computed(() => {
    const map = new Map<string, ShortDocument[]>();
    for (const short of allShorts.value) {
      const authorId = short.created_by;
      if (!map.has(authorId)) {
        map.set(authorId, []);
      }
      map.get(authorId)!.push(short);
    }
    return map;
  });

  // Automatically listen for native recording transfer events from Flutter
  onShortTransferStart(({ totalBytes }) => {
    isTransferringFromNative.value = true;
    nativeTransferProgress.value = 0;
    nativeTransferTotalBytes.value = totalBytes;
    recordedVideoFile.value = null;
  });

  onShortTransferProgress(({ progress }) => {
    nativeTransferProgress.value = progress;
  });

  onShortRecorded((file: File) => {
    isTransferringFromNative.value = false;
    nativeTransferProgress.value = 1;
    recordedVideoFile.value = file;
  });

  onShortTransferError(() => {
    isTransferringFromNative.value = false;
    nativeTransferProgress.value = 0;
    setErrorToast({
      message: translate("pages.shorts.transfer_failed"),
    });
  });

  onShortCameraDismissed(() => {
    isTransferringFromNative.value = false;
    nativeTransferProgress.value = 0;
  });

  /* Real-time listener */
  function subscribeToGroup(groupId: string) {
    if (subscribedGroupId === groupId && activeUnsubscribe) {
      return;
    }

    if (activeUnsubscribe) {
      activeUnsubscribe();
      activeUnsubscribe = null;
      subscribedGroupId = null;
    }

    subscribedGroupId = groupId;

    // Check if we already have in-memory shorts for this group
    const hasInMemoryShorts = Boolean(shortsByGroup.value[groupId]?.length);

    if (!hasInMemoryShorts) {
      loadingByGroup.value[groupId] = true;
      // Immediately read from IndexedDB cache to populate the store instantaneously
      getCachedDocuments<ShortDocument>("shorts", groupId)
        .then((cached) => {
          // Only populate from cache if server hasn't already responded for this group
          if (
            cached &&
            cached.length > 0 &&
            isStaleByGroup.value[groupId] !== false
          ) {
            shortsByGroup.value[groupId] = cached;
            loadingByGroup.value[groupId] = false;
            isStaleByGroup.value[groupId] = true;
          }
        })
        .catch((error) => {
          console.error("Error reading cached shorts from IndexedDB:", error);
        });
    }

    activeUnsubscribe = subscribeToShortsService(groupId, {
      onShorts: (newShorts, metadata) => {
        shortsByGroup.value[groupId] = newShorts;
        loadingByGroup.value[groupId] = false;

        if (metadata && !metadata.fromCache) {
          // Fresh server response received
          isStaleByGroup.value[groupId] = false;
        } else if (isStaleByGroup.value[groupId] !== false) {
          // Cached response from Firestore local cache
          isStaleByGroup.value[groupId] = true;
        }

        // Persist fresh data to IndexedDB
        setCachedDocuments("shorts", groupId, newShorts).catch((error) => {
          console.error("Error saving shorts to IndexedDB cache:", error);
        });

        // Check for redacted shorts and proactively purge their video and thumbnail from memory and cache
        for (const short of newShorts) {
          if (short.redacted) {
            if (videosById.value[short._id]) {
              URL.revokeObjectURL(videosById.value[short._id]!.url);
              delete videosById.value[short._id];
              videoAccessOrder = videoAccessOrder.filter(
                (id) => id !== short._id,
              );
            }
            if (thumbnailsById.value[short._id]) {
              URL.revokeObjectURL(thumbnailsById.value[short._id]!.url);
              delete thumbnailsById.value[short._id];
            }
            inFlightVideoLoads.delete(short._id);
            inFlightThumbnailLoads.delete(short._id);

            clearCachedImages(`${CacheTypes.ShortVideo}:${short._id}`)?.catch?.(
              () => {},
            );
            clearCachedImages(
              `${CacheTypes.ShortThumbnail}:${short._id}`,
            )?.catch?.(() => {});
          }
        }
      },
      onError: (error: FirestoreError) => {
        const message = handleFirestoreException({ e: error });
        setErrorToast({ message });
        loadingByGroup.value[groupId] = false;
      },
    });
  }

  // Reactively subscribe when active group changes
  watch(
    () => userStore.activeGroup,
    (newGroupId) => {
      if (newGroupId) {
        subscribeToGroup(newGroupId);
      } else if (activeUnsubscribe) {
        activeUnsubscribe();
        activeUnsubscribe = null;
        subscribedGroupId = null;
      }
    },
    { immediate: true },
  );

  function markAsViewed(shortId: string) {
    if (!sessionViewedShortIds.value.has(shortId)) {
      const updated = new Set(sessionViewedShortIds.value);
      updated.add(shortId);
      sessionViewedShortIds.value = updated;
    }

    const currentUid = authStore.authUuid;
    const currentGroup = activeGroupId.value;
    if (currentUid && currentGroup) {
      const found = shorts.value.find((s) => s._id === shortId);
      if (!found?.viewed_by?.includes(currentUid)) {
        markShortAsViewedService(currentGroup, shortId, currentUid);
        if (found) {
          if (!found.viewed_by) {
            found.viewed_by = [];
          }
          found.viewed_by.push(currentUid);
        }
      }
    }
  }

  function isViewed(short: ShortDocument | string): boolean {
    const currentUid = authStore.authUuid;
    const doc =
      typeof short === "string"
        ? shorts.value.find((s) => s._id === short)
        : short;
    if (!doc) {
      const id = typeof short === "string" ? short : short._id;
      return sessionViewedShortIds.value.has(id);
    }
    return Boolean(
      (currentUid && doc.viewed_by?.includes(currentUid)) ||
      sessionViewedShortIds.value.has(doc._id),
    );
  }

  const unviewedShorts = computed<ShortDocument[]>(() => {
    return shorts.value.filter((s) => !isViewed(s));
  });

  const unviewedCount = computed<number>(() => {
    return unviewedShorts.value.length;
  });

  const hasUnviewedShorts = computed<boolean>(() => {
    return unviewedCount.value > 0;
  });

  async function resolveVideoUrl(short: ShortDocument): Promise<string> {
    if (short.redacted || (short.reported_by?.length ?? 0) > 0) {
      return "";
    }

    if (videosById.value[short._id]) {
      // Move to end of access order (most recently used)
      videoAccessOrder = videoAccessOrder.filter((id) => id !== short._id);
      videoAccessOrder.push(short._id);
      return videosById.value[short._id]!.url;
    }

    const existing = inFlightVideoLoads.get(short._id);
    if (existing) {
      return existing;
    }

    const promise = (async () => {
      try {
        const result = await getShortVideoFromFirestorage(short);

        // Evict oldest video if at capacity (SHORT_IN_MEMORY_MAX_VIDEOS = 4)
        while (videoAccessOrder.length >= SHORT_IN_MEMORY_MAX_VIDEOS) {
          // Never evict currently active playing short or the short currently resolving
          const evictCandidateIndex = videoAccessOrder.findIndex(
            (id) => id !== activeShort.value?._id && id !== short._id,
          );
          if (evictCandidateIndex === -1) {
            break;
          }
          const [evictedId] = videoAccessOrder.splice(evictCandidateIndex, 1);
          if (evictedId && videosById.value[evictedId]) {
            URL.revokeObjectURL(videosById.value[evictedId]!.url);
            delete videosById.value[evictedId];
          }
        }

        videosById.value[short._id] = markRaw({
          blob: result.blob,
          url: result.url,
        });
        videoAccessOrder = videoAccessOrder.filter((id) => id !== short._id);
        videoAccessOrder.push(short._id);

        return result.url;
      } catch (e) {
        console.error(
          "Failed to download short video as blob, falling back to download URL:",
          e,
        );
        try {
          const storageReference = storageRef(
            firebaseStorage,
            short.absolute_video_url,
          );
          const fallbackUrl = await getDownloadURL(storageReference);
          return fallbackUrl;
        } catch (fallbackError) {
          console.error(
            "Failed fallback download URL for short video:",
            fallbackError,
          );
          return "";
        }
      }
    })().finally(() => {
      inFlightVideoLoads.delete(short._id);
    });

    inFlightVideoLoads.set(short._id, promise);
    return promise;
  }

  async function resolveThumbnailUrl(short: ShortDocument): Promise<string> {
    if (short.redacted || (short.reported_by?.length ?? 0) > 0) {
      return "";
    }

    if (thumbnailsById.value[short._id]) {
      return thumbnailsById.value[short._id]!.url;
    }
    if (!short.absolute_thumbnail_url) return "";

    const existing = inFlightThumbnailLoads.get(short._id);
    if (existing) {
      return existing;
    }

    const promise = (async () => {
      try {
        const result = await getShortThumbnailFromFirestorage(short);
        thumbnailsById.value[short._id] = markRaw({
          blob: result.blob,
          url: result.url,
        });
        return result.url;
      } catch (e) {
        console.error(
          "Failed to download short thumbnail as blob, falling back to download URL:",
          e,
        );
        try {
          const storageReference = storageRef(
            firebaseStorage,
            short.absolute_thumbnail_url,
          );
          const fallbackUrl = await getDownloadURL(storageReference);
          return fallbackUrl;
        } catch (fallbackError) {
          console.error(
            "Failed fallback download URL for short thumbnail:",
            fallbackError,
          );
          return "";
        }
      }
    })().finally(() => {
      inFlightThumbnailLoads.delete(short._id);
    });

    inFlightThumbnailLoads.set(short._id, promise);
    return promise;
  }

  function prefetchVideo(short?: ShortDocument): void {
    if (
      !short ||
      short.redacted ||
      !short.absolute_video_url ||
      (short.reported_by?.length ?? 0) > 0
    )
      return;
    if (videosById.value[short._id]) return;
    resolveVideoUrl(short).catch(() => {});
  }

  function pruneVideoMemory(preserveShortIds: string[] = []): void {
    const preserveSet = new Set(preserveShortIds);
    const remainingOrder: string[] = [];
    for (const id of videoAccessOrder) {
      if (preserveSet.has(id)) {
        remainingOrder.push(id);
      } else if (videosById.value[id]) {
        URL.revokeObjectURL(videosById.value[id]!.url);
        delete videosById.value[id];
      }
    }
    videoAccessOrder = remainingOrder;
  }

  function evictVideo(shortId: string): void {
    if (videosById.value[shortId]) {
      URL.revokeObjectURL(videosById.value[shortId]!.url);
      delete videosById.value[shortId];
      videoAccessOrder = videoAccessOrder.filter((id) => id !== shortId);
    }
    inFlightVideoLoads.delete(shortId);
  }

  function revokeMediaUrls(): void {
    for (const item of Object.values(videosById.value)) {
      URL.revokeObjectURL(item.url);
    }
    videosById.value = {};
    videoAccessOrder = [];

    for (const item of Object.values(thumbnailsById.value)) {
      URL.revokeObjectURL(item.url);
    }
    thumbnailsById.value = {};

    inFlightVideoLoads.clear();
    inFlightThumbnailLoads.clear();
  }

  onCacheInvalidated((keyPattern) => {
    if (keyPattern === "*") {
      revokeMediaUrls();
      return;
    }
    if (keyPattern.startsWith(`${CacheTypes.ShortVideo}:`)) {
      const shortId = keyPattern.split(":")[1];
      if (shortId && videosById.value[shortId]) {
        URL.revokeObjectURL(videosById.value[shortId]!.url);
        delete videosById.value[shortId];
        videoAccessOrder = videoAccessOrder.filter((id) => id !== shortId);
      }
    } else if (keyPattern.startsWith(`${CacheTypes.ShortThumbnail}:`)) {
      const shortId = keyPattern.split(":")[1];
      if (shortId && thumbnailsById.value[shortId]) {
        URL.revokeObjectURL(thumbnailsById.value[shortId]!.url);
        delete thumbnailsById.value[shortId];
        inFlightThumbnailLoads.delete(shortId);

        // Re-resolve thumbnail for active shorts (skip if redacted)
        const short =
          allShorts.value.find((s) => s._id === shortId) ??
          Object.values(shortsByGroup.value)
            .flat()
            .find((s) => s._id === shortId);
        if (short && !short.redacted) {
          resolveThumbnailUrl(short).catch(() => {});
        }
      }
    }
  });

  function openViewer(short: ShortDocument) {
    openedShort.value = short;
    markAsViewed(short._id);
    viewerOpen.value = true;
  }

  function closeViewer() {
    viewerOpen.value = false;
    openedShort.value = null;
  }

  function setRecordedVideoFile(file: File | null) {
    recordedVideoFile.value = file;
  }

  async function addShort(
    groupId: string,
    data: Omit<ShortDocument, "_id">,
  ): Promise<string> {
    const id = await addShortService(groupId, data);
    markAsViewed(id);
    return id;
  }

  async function deleteShort(
    groupId: string,
    short: ShortDocument,
  ): Promise<void> {
    const shortId = short._id;
    // Document first: a failed Storage delete then only orphans a file, never a doc pointing at missing media
    await deleteShortService(groupId, shortId);
    await deleteShortMedia(
      [short.absolute_video_url, short.absolute_thumbnail_url].filter(
        (path): path is string => Boolean(path),
      ),
    );
    if (activeShort.value?._id === shortId) {
      closeViewer();
    }
    if (videosById.value[shortId]) {
      URL.revokeObjectURL(videosById.value[shortId]!.url);
      delete videosById.value[shortId];
      videoAccessOrder = videoAccessOrder.filter((id) => id !== shortId);
    }
    if (thumbnailsById.value[shortId]) {
      URL.revokeObjectURL(thumbnailsById.value[shortId]!.url);
      delete thumbnailsById.value[shortId];
    }
    clearCachedImages(`${CacheTypes.ShortVideo}:${shortId}`);
    clearCachedImages(`${CacheTypes.ShortThumbnail}:${shortId}`);
  }

  async function reportShort(
    groupId: string,
    shortId: string,
    reporterId: string,
  ): Promise<void> {
    await reportShortService(groupId, shortId, reporterId);
  }

  function reset(): void {
    if (activeUnsubscribe) {
      activeUnsubscribe();
      activeUnsubscribe = null;
    }
    subscribedGroupId = null;
    shortsByGroup.value = {};
    loadingByGroup.value = {};
    isStaleByGroup.value = {};
    openedShort.value = null;
    isTransferringFromNative.value = false;
    nativeTransferProgress.value = 0;
    nativeTransferTotalBytes.value = 0;
    recordedVideoFile.value = null;
    sessionViewedShortIds.value = new Set();
    try {
      localStorage.removeItem("kekkle_viewed_shorts");
    } catch {
      // Ignore
    }
    revokeMediaUrls();
  }

  function abortTransfer(): void {
    abortActiveShortTransfer();
    isTransferringFromNative.value = false;
    nativeTransferProgress.value = 0;
    nativeTransferTotalBytes.value = 0;
  }

  return {
    allShorts,
    shorts,
    loading,
    isStale,
    isStaleByGroup,
    unviewedShorts,
    unviewedCount,
    hasUnviewedShorts,
    shortsByAuthor,
    isTransferringFromNative,
    nativeTransferProgress,
    nativeTransferTotalBytes,
    abortTransfer,
    recordedVideoFile,
    activeShort,
    viewerOpen,
    viewedShortIds: sessionViewedShortIds,
    videosById,
    thumbnailsById,
    subscribeToGroup,
    markAsViewed,
    isViewed,
    resolveVideoUrl,
    resolveThumbnailUrl,
    prefetchVideo,
    revokeMediaUrls,
    pruneVideoMemory,
    evictVideo,
    openViewer,
    closeViewer,
    setRecordedVideoFile,
    addShort,
    deleteShort,
    reportShort,
    reset,
  };
});
